import { Breadcrumbs, ContentLayout } from 'components/Layout';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, SecureText } from 'components/Elements';
import { ChevronLeft } from 'react-feather';
import { useCustomerDetails } from '../api/getCustomerDetails';
import { formatAccountAge } from 'utils/formatAccountAge';
import { formatDate } from 'utils/format';
import Lottie from 'lottie-react';
import noKycAnimation from 'assets/lottie/connectionlost.json';
import { useEffect, useState } from 'react';
import {
  DepositToCustomerWallet,
  useCustomerWallets,
  WithdrawToCustomer,
} from 'features/transactions';
import { WalletsList } from './CustomerDetails/WalletsList';
import { TotalTransactions } from './CustomerDetails/TotalTransactions';
import { TotalAccountingBalance } from './CustomerDetails/TotalAccountingBalance';
import { TransactionHistory } from './CustomerDetails/TransactionHistory';
import { Badges } from '../component/Badges';
import { Authorization, ROLES } from 'lib/authorization';
import { imageToBlob } from 'utils/ImageToBlob';
import { ShieldCheck } from 'lucide-react';
import backgroundUrl from 'assets/user-banner-1.jpg';

const CenteredInfo = ({ label, value }) => (
  <div className="text-start space-y-1">
    <div className="text-white/50 text-xs uppercase tracking-wider">
      {label}
    </div>
    <div className="text-white font-bold text-sm">
      {value || <em className="text-gray-400">Missing</em>}
    </div>
  </div>
);

const ProfileCard = ({ user }) => {
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    const fetchImageBlob = async () => {
      if (user?.photo) {
        const blobUrl = await imageToBlob(user.photo);
        if (blobUrl) setImageUrl(blobUrl);
      }
    };
    fetchImageBlob();
  }, [user?.photo]);

  const initials = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`;

  return (
    <div className="relative w-full mb-12">
      <div
        className="w-full h-48 sm:h-60 md:h-72 bg-cover bg-center bg-white/10 backdrop-blur-md rounded-3xl border border-white/10"
        style={{ backgroundImage: `url(${backgroundUrl})` }}
      />
      <div className="absolute top-2/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <div className="w-32 h-32 rounded-full bg-white/10 backdrop-blur-md border border-white/10 overflow-hidden flex items-center justify-center">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Profile"
              className="w-full h-full object-cover rounded-full"
            />
          ) : (
            <span className="text-3xl text-white font-semibold">
              {initials}
            </span>
          )}
        </div>
      </div>
      <div className="mt-20 flex justify-center items-center space-x-2">
        <ShieldCheck
          className={
            user.allowAuthenticator ? 'text-green-500' : 'text-white/40'
          }
        />
        <span className="text-white text-sm">
          2FA {user.allowAuthenticator ? 'Enabled' : 'Disabled'}
        </span>
      </div>
    </div>
  );
};

export const User = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const customerDetailsQuery = useCustomerDetails({ id });
  const customerWalletsQuery = useCustomerWallets({ customerId: id });

  if (customerDetailsQuery.isLoading || customerWalletsQuery.isLoading) {
    return (
      <ContentLayout title="User details" breadcrumbs={<Breadcrumbs />}>
        <div className="px-6 animate-pulse space-y-6">
          <div className="w-40 h-40 rounded-full bg-white/10 mx-auto" />
          <div className="h-4 w-20 mx-auto bg-white/10 rounded" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-12 bg-white/10 rounded" />
            ))}
          </div>
          <div className="h-20 bg-white/10 rounded-xl" />
          <div className="h-20 bg-white/10 rounded-xl" />
          <div className="h-40 bg-white/10 rounded-xl" />
          <div className="h-64 bg-white/10 rounded-xl" />
        </div>
      </ContentLayout>
    );
  }

  console.log(customerDetailsQuery.data?.userDetails);

  const userDetails = customerDetailsQuery.data?.userDetails || {};
  const wallets = customerWalletsQuery.data?.walletDetails || [];
  const transactions = [
    ...(customerDetailsQuery.data?.userDeposits?.depositTransactions?.flatMap(
      (tx) => Object.values(tx.transactions).flat()
    ) || []),
    ...(customerDetailsQuery.data?.userWithdraws?.withdrawTransactions?.flatMap(
      (tx) => Object.values(tx.transactions).flat()
    ) || []),
    ...(customerDetailsQuery.data?.userSwaps?.swapTransactions?.successful ||
      []),
    ...(customerDetailsQuery.data?.userSwaps?.swapTransactions?.failed || []),
    ...(customerDetailsQuery.data?.userTransfers?.transferTransactions
      ?.successful || []),
    ...(customerDetailsQuery.data?.userTransfers?.transferTransactions
      ?.failed || []),
  ];

  if (!userDetails.id) {
    return (
      <ContentLayout title="User details" breadcrumbs={<Breadcrumbs />}>
        <div className="flex flex-col items-center justify-center min-h-[50vh] text-center space-y-4 pb-12">
          <div className="w-full max-w-sm">
            <Lottie animationData={noKycAnimation} loop={true} />
          </div>
          <p className="text-3xl font-bold text-white">
            User details unavailable
          </p>
          <p className="text-gray-400 max-w-xl">
            We couldn’t find the information right now. Try again later or
            contact support.
          </p>
          <Button
            onClick={() => navigate(-1)}
            startIcon={<ChevronLeft className="mr-2 w-4 h-4" />}
          >
            Go Back
          </Button>
        </div>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      title="User details"
      end={
        <Authorization
          allowedRoles={[ROLES.ROOT_USER, ROLES.TRANSACTION_MANAGER]}
          forbiddenFallback={<div />}
        >
          <div className="flex items-center space-x-4">
            <WithdrawToCustomer
              customerId={id}
              customerName={`${userDetails.firstName} ${userDetails.middleName} ${userDetails.lastName}`}
            />
            <DepositToCustomerWallet
              customerId={id}
              customerName={`${userDetails.firstName} ${userDetails.middleName} ${userDetails.lastName}`}
            />
          </div>
        </Authorization>
      }
      breadcrumbs={<Breadcrumbs />}
    >
      <div className="px-6 space-y-8">
        <ProfileCard user={userDetails} />

        <div className="">
          <div className="grid grid-cols-2 gap-4">
            <div className="border border-white/10 bg-white/10 rounded-3xl p-4 mb-4">
              <div className="font-bold mb-3">Personal Details</div>
              <CenteredInfo
                label="First Name"
                value={`${userDetails.firstName}`}
              />
              <CenteredInfo
                label="Middle Name"
                value={`${userDetails.middleName}`}
              />
              <CenteredInfo
                label="Last Name"
                value={`${userDetails.lastName}`}
              />
              <CenteredInfo
                label="Wire24 ID"
                value={
                  <SecureText
                    value={userDetails.userCode}
                    allowedRoles={[ROLES.ROOT_USER]}
                    className="text-sm"
                  />
                }
              />
              <CenteredInfo label="Username" value={userDetails.userName} />
              <CenteredInfo
                label="Gender"
                value={userDetails.gender === 'm' ? 'Male' : 'Female'}
              />
              <CenteredInfo
                label="Date of Birth"
                value={formatDate(userDetails.dob)}
              />
              <CenteredInfo
                label="Email"
                value={
                  <SecureText
                    value={userDetails.email}
                    allowedRoles={[ROLES.ROOT_USER]}
                    className="text-sm"
                  />
                }
              />
              <CenteredInfo
                label="Phone"
                value={
                  <SecureText
                    value={userDetails.phoneNumber}
                    allowedRoles={[ROLES.ROOT_USER]}
                    className="text-sm"
                  />
                }
              />
            </div>
            <div className="">
              <div className="border bg-white/10 border-white/10 rounded-3xl p-4 mb-4">
                <div className="font-bold mb-3">Address Information</div>
                <CenteredInfo
                  label="Street Address"
                  value={userDetails.address}
                />
                <CenteredInfo
                  label="Apartment Number"
                  value={userDetails.apartmentNumber}
                />
                <CenteredInfo label="City / Town" value={userDetails.city} />
                <CenteredInfo
                  label="State / Province / Region"
                  value={userDetails.state}
                />
                <CenteredInfo
                  label="Postal / ZIP Code"
                  value={userDetails.state}
                />
              <CenteredInfo label="Country" value={userDetails.country} />
              </div>

              <div className="border border-white/10 bg-white/10 rounded-3xl p-4 mb-4">
                <CenteredInfo
                  label="Account Age"
                  value={formatAccountAge(userDetails.createdAt)}
                />
              </div>
            </div>
          </div>
        </div>

        <Badges firstName={userDetails.firstName} />

        <div className="grid sm:grid-cols-1 md:grid-cols-2 gap-6">
          <TotalTransactions transactionsCount={transactions.length} />
          <TotalAccountingBalance wallets={wallets} />
        </div>

        <Authorization
          allowedRoles={[ROLES.ROOT_USER, ROLES.TRANSACTION_MANAGER]}
          forbiddenFallback={<div />}
        >
          <WalletsList wallets={wallets} />
        </Authorization>

        <Authorization
          allowedRoles={[
            ROLES.ROOT_USER,
            ROLES.TRANSACTION_MANAGER,
            ROLES.KYC_AGENT,
          ]}
          forbiddenFallback={<div />}
        >
          <TransactionHistory transactions={transactions} />
        </Authorization>
      </div>
    </ContentLayout>
  );
};
